import { Box, Stack } from "@mui/material";

import { Button, type ButtonVariant } from "./Button";
import { type IconType } from "./Icon";
import { LoadingButton } from "./LoadingButton";

export const DIALOG_FOOTER_LAYOUTS = ["horizontal", "vertical"] as const;

export type DialogFooterLayout = (typeof DIALOG_FOOTER_LAYOUTS)[number];

export interface DialogFooterAction {
  /**
   * Button label text
   */
  label: string;
  /**
   * Style variant
   */
  variant?: ButtonVariant;
  /**
   * Whether the button should be disabled
   */
  disabled?: boolean;
  /**
   * Click handler
   */
  onClick: () => void;
}

export interface DialogFooterPrimaryAction extends DialogFooterAction {
  /**
   * Optional icon to show before the label
   */
  startIconType?: IconType;
  /**
   * Whether the button should show loading state
   */
  isLoading?: boolean;
  /**
   * Whether the button should be disabled
   */
  disabled?: boolean;
}

export interface DialogFooterProps {
  /**
   * Layout direction for buttons
   * - horizontal: Cancel on left, Primary on right (default)
   * - vertical: Primary on top (full width), Cancel below (full width)
   */
  layout?: DialogFooterLayout;
  /**
   * Primary action button configuration (Save, Confirm, etc.)
   */
  primaryAction: DialogFooterPrimaryAction;
  /**
   * Optional secondary action button configuration (Cancel, Close, etc.)
   */
  secondaryAction?: DialogFooterAction;
}

/**
 * Dialog footer component with configurable button layouts.
 *
 * Supports two layouts matching the Figma design system:
 * - horizontal: Side-by-side buttons with Cancel on left, Primary on right
 * - vertical: Stacked buttons with Primary on top, Cancel below (both full width)
 */
export function DialogFooter(props: DialogFooterProps) {
  const { layout = "horizontal", primaryAction, secondaryAction } = props;

  const primaryButton = primaryAction.isLoading ? (
    <LoadingButton
      fullWidth={layout === "vertical"}
      isLoading={primaryAction.isLoading}
      disabled={primaryAction.disabled}
      startIconType={primaryAction.startIconType}
      variant={primaryAction.variant ?? "primary"}
      onClick={primaryAction.onClick}
    >
      {primaryAction.label}
    </LoadingButton>
  ) : (
    <Button
      fullWidth={layout === "vertical"}
      disabled={primaryAction.disabled}
      startIconType={primaryAction.startIconType}
      variant={primaryAction.variant ?? "primary"}
      onClick={primaryAction.onClick}
    >
      {primaryAction.label}
    </Button>
  );

  const secondaryButton = secondaryAction ? (
    <Button
      fullWidth={layout === "vertical"}
      disabled={secondaryAction.disabled}
      variant={secondaryAction.variant ?? "outlined"}
      onClick={secondaryAction.onClick}
    >
      {secondaryAction.label}
    </Button>
  ) : null;

  if (layout === "vertical") {
    return (
      <Box
        sx={(theme) => ({
          paddingX: theme.spacing(5),
          paddingY: theme.spacing(3),
        })}
      >
        <Stack spacing={3}>
          {primaryButton}
          {secondaryButton}
        </Stack>
      </Box>
    );
  }

  // Horizontal layout (default)
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingX: theme.spacing(5),
        paddingY: theme.spacing(3),
      })}
    >
      {secondaryButton}
      {primaryButton}
    </Box>
  );
}
